import { cn } from '@neo/lib/utils'
import { Button } from '@neo/primitives/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@neo/primitives/dropdown-menu'
import { ChevronDown, CircleCheck, CirclePlus } from 'lucide-react'
import type { RCard } from 'v2/types/api/billing'
import { CardRowActions } from './card-row-actions'
import { PaymentCard } from './cards-list'
import { ScrollFadeList } from './scroll-fade-list'

interface CardSelectorProps {
  activeCards: RCard[]
  selectedCard: number
  onCardChange: (cardId: number) => void
  onAddCard: () => void
  isPending?: boolean
  showAddCardButton?: boolean
  showAddCardLink?: boolean
  title?: string
  // Drops this component's own outline and title row so it can be nested inside
  // a block that owns them — the Card payment-mode tile, which the card list
  // belongs to. Standalone (default) it keeps its own outline.
  embedded?: boolean
  // Second line under the card number in the trigger row. When the saved card IS
  // the Card payment-mode row, the mode's fee belongs there — the same place the
  // other modes carry theirs.
  badge?: string
  // Fired when the trigger row is clicked, alongside opening the menu — lets the
  // host treat "touched the card" as "chose to pay by card".
  onTriggerClick?: () => void
}

// The standalone empty state uses the shared action treatment, matching the
// Add card command on Payment methods while card details remain body text.
//
// "Add card", not "Add a new card": with nothing saved this is not an addition to
// a list, it IS the payment method. The "a new" wording belongs to AddCardMenuItem,
// which only ever renders under saved cards.
const AddCardRow = ({ onAddCard, isPending }: { onAddCard: () => void; isPending: boolean }) => (
  <Button
    type="button"
    variant="ghost"
    onClick={onAddCard}
    disabled={isPending}
    data-testid="button-add-card"
    className="h-auto w-full justify-start gap-2 px-3 py-2.5 hover:bg-hv1"
  >
    <CirclePlus className="size-4 shrink-0 text-secondary" />
    Add card
  </Button>
)

// Same affordance as AddCardRow, but as a menu item — it lives at the FOOT OF THE
// PICKER, under the saved cards, not as a row hanging off the collapsed trigger.
// A permanent row under the trigger reads as a second payment method sitting
// inside the Card block; inside the menu it reads as the last option in the list
// of cards, which is what it is. DropdownMenuItem rather than a bare button so it
// takes keyboard nav and closes the menu on select like every other row.
const AddCardMenuItem = ({ onAddCard }: { onAddCard: () => void }) => (
  <DropdownMenuItem
    onSelect={onAddCard}
    data-testid="button-add-card"
    className="gap-2 px-3 py-2.5 font-jetbrains text-label uppercase"
  >
    <CirclePlus className="size-4 shrink-0 text-secondary" />
    Add a new card
  </DropdownMenuItem>
)

const SelectorTitle = ({
  title,
  showAddCardLink,
  onAddCard,
}: {
  title: string
  showAddCardLink: boolean
  onAddCard: () => void
}) => (
  <div className="flex items-center justify-between">
    <h3 className="text-p-600">{title}</h3>
    {showAddCardLink && (
      <Button
        variant="link"
        className="text-xs-600 p-0 h-auto"
        onClick={onAddCard}
        data-testid="link-add-card"
      >
        Add Card
      </Button>
    )}
  </div>
)

export const CardSelector = ({
  activeCards,
  selectedCard,
  onCardChange,
  onAddCard,
  isPending = false,
  showAddCardButton = false,
  showAddCardLink = false,
  title,
  embedded = false,
  badge,
  onTriggerClick,
}: CardSelectorProps) => {
  const hasSavedCards = activeCards.length > 0

  if (!hasSavedCards) {
    // Embedded and empty, this renders NOTHING. Inside the Card payment-mode tile
    // the tile itself already says "Credit/Debit Card" and the dialog's primary
    // button is what collects the first card, so an "Add card" row under it is a
    // second control for a job that already has one.
    //
    // Standalone there is no such button, so the action is the whole content and
    // it keeps the outline — the block then doesn't change shape once the first
    // card lands in it.
    if (embedded) return null
    return (
      <div className="space-y-4">
        {title && (
          <SelectorTitle title={title} showAddCardLink={showAddCardLink} onAddCard={onAddCard} />
        )}
        <div className="overflow-hidden rounded border border-border">
          <AddCardRow onAddCard={onAddCard} isPending={isPending} />
        </div>
      </div>
    )
  }

  // The trigger always has to show a card, but `selectedCard` can briefly be 0 (or
  // a stale id) before the parent's effect settles on one — fall back to the
  // default card, then the first, so the trigger never renders blank.
  const selectedCardData =
    activeCards.find((card) => card.card_mapping_id === selectedCard) ??
    activeCards.find((card) => card.default) ??
    activeCards[0]

  return (
    <div className={cn(!embedded && 'space-y-4')}>
      {title && !embedded && (
        <SelectorTitle title={title} showAddCardLink={showAddCardLink} onAddCard={onAddCard} />
      )}
      {/* Collapsed to a dropdown rather than a radio list: the list grew with the
          number of saved cards and pushed the dialog past the viewport. The trigger
          keeps the same card row the Payment methods page uses (brand tile, masked
          number, Default pill) so the selected card still reads as a card. */}
      {/* Selected card and "add a card" share one outline, split by a hairline,
          rather than sitting as two stacked bordered boxes — two outlines read as
          two unrelated controls when they're really one payment-method block. */}
      <div className={cn(!embedded && 'overflow-hidden rounded border border-border')}>
        <DropdownMenu>
          {/* The WHOLE row is the trigger, chevron included — clicking the card
              opens the picker and clicking it again closes it (Radix toggles on
              the trigger). A chevron-only hit area asked the customer to find a
              28px target to do the row's one obvious job, and left the other 90%
              of the row inert.
              `onTriggerClick` rides along rather than competing with it: where the
              row doubles as the Card payment-mode tile, touching the card is also
              how that mode gets chosen. Firing on the closing click too is
              harmless — re-selecting the mode already chosen is a no-op. */}
          <DropdownMenuTrigger asChild disabled={isPending}>
            <button
              type="button"
              disabled={isPending}
              onClick={onTriggerClick}
              data-testid="card-selector-trigger"
              className={cn(
                'flex w-full items-center gap-2 text-left transition-colors hover:bg-hv1 disabled:cursor-not-allowed disabled:opacity-50',
                // Inset matches MethodTile's px-3 when this row IS a payment mode,
                // so the brand logo lines up with the other modes' icons; the
                // standalone picker keeps its tighter original padding.
                onTriggerClick ? 'px-3 py-3' : 'px-1.5 py-1.5',
              )}
            >
              <div className="min-w-0 flex-1">
                <PaymentCard
                  showDelete={false}
                  size={36}
                  enableDefault={false}
                  card={selectedCardData}
                  variant="recharge-modal"
                  subLabel={badge}
                />
              </div>
              <ChevronDown className="size-4 shrink-0 text-secondary" />
            </button>
          </DropdownMenuTrigger>
          {/* Matches the trigger's width so the rows line up with it, and caps the
              height so any number of saved cards scrolls inside the menu instead of
              growing the dialog. Padding moves to the scroll container so the edge
              fades span the menu's full width. */}
          <DropdownMenuContent
            align="start"
            collisionPadding={12}
            className="w-[var(--radix-dropdown-menu-trigger-width)] p-0"
          >
            {/* Capped by whatever room Radix reports below the row, so the list
                scrolls rather than growing tall enough to be shoved back over the
                card it belongs to. */}
            <ScrollFadeList className="max-h-[min(16rem,var(--radix-dropdown-menu-content-available-height))] overflow-y-auto p-1">
              {activeCards.map((card) => (
                // The row's actions menu sits beside the selectable item, not
                // inside it — see CardRowActions for why.
                // Hairline between rows (not after the last): with three or four
                // cards stacked the brand logos and masked numbers repeat closely
                // enough that the eye needs the rule to tell one card from the
                // next.
                <div
                  key={card.card_mapping_id}
                  className="flex items-center gap-1 border-border border-b pr-1 last:border-b-0"
                >
                  <DropdownMenuItem
                    onSelect={() => onCardChange(card.card_mapping_id)}
                    data-testid={`card-selector-option-${card.card_mapping_id}`}
                    className="min-w-0 flex-1 gap-2"
                  >
                    <div className="min-w-0 flex-1">
                      <PaymentCard
                        showDelete={false}
                        size={36}
                        enableDefault={false}
                        card={card}
                        variant="recharge-modal"
                      />
                    </div>
                    {/* Held in the layout on every row (invisible when unselected)
                        so the pill / check / actions columns line up down the list
                        instead of shifting as the selection moves.
                        Explicit emerald pair, not `text-success`: that token is a
                        bright green in light mode but a dark teal in dark mode, so
                        the tick would all but vanish on the dark theme
                        (CLAUDE.md failure mode 23). */}
                    <CircleCheck
                      className={cn(
                        'size-4 shrink-0 text-emerald-600 dark:text-emerald-400',
                        card.card_mapping_id === selectedCardData.card_mapping_id
                          ? 'opacity-100'
                          : 'opacity-0',
                      )}
                    />
                  </DropdownMenuItem>
                  <CardRowActions card={card} disabled={isPending} />
                </div>
              ))}
            </ScrollFadeList>
            {/* Outside the scroll container, so it stays pinned at the foot of the
                menu while the cards scroll past it. */}
            {showAddCardButton && (
              <>
                <DropdownMenuSeparator className="mx-0 my-0" />
                <AddCardMenuItem onAddCard={onAddCard} />
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}
